import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  inject,
  isDevMode,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, Validators } from '@angular/forms';

import {
  EnhancementVariantDetail,
  IEnhancementVariantAsset,
  IMasterSuite,
} from 'src/app/models/mastersuites/mastersuite';
import { FilenameConventionEvBased } from 'src/app/models/filename-conventions/filename-convention-payload';
import { FilenameConventionPerSelect } from 'src/app/models/filename-conventions/filename-convention-per-select';
import {
  FilenameConventionWriteReason,
  FilenameConventionWriteReasonKind,
} from 'src/app/models/filename-conventions/filename-convention-write-reason';
import { TokenFormat, TokenFormatOrigin } from 'src/app/models/filename-conventions/token-format';
import {
  FilenameConventionPreviewService,
  RenderResult,
} from 'src/app/services/filename-conventions/filename-convention-preview.service';
import { FILENAME_TOKEN_VOCABULARY_WITH_CUSTOM } from '../../shared/token-chip-picker/token-vocabulary';
import { TokenVocabularyItem } from '../../shared/token-chip-picker/token-chip-picker.component';
import { chipLabelOf, classifyChip } from '../../shared/chip-classifier';

export type EvConventionEditorMode = 'create' | 'update';

export type EvConventionEditorReasonKind = Exclude<
  FilenameConventionWriteReasonKind,
  'initialCreate' | 'deleted'
>;

export interface EvConventionMsEv {
  masterSuiteId: string;
  masterSuiteEnhancementVariantId: string;
}

export interface EvConventionPerSelectRow {
  asset: IEnhancementVariantAsset;
  tokenFormats: TokenFormat[];
  preview: RenderResult;
}

export interface EvConventionEditorPayload {
  mode: 'evBased';
  conventionMsEv: EvConventionMsEv | null;
  template: TokenFormat[];
  perSelectRows: FilenameConventionPerSelect[];
  writeReason: FilenameConventionWriteReason | null;
}

interface LegacyPerSelect {
  masterSuiteEnhancementVariantAssetId: string;
  customValues?: string[];
  extraChips?: TokenFormat[];
  tokenFormats?: TokenFormat[];
}

const SELECTABLE_REASONS: readonly EvConventionEditorReasonKind[] = [
  'preferenceUpdate',
  'correction',
  'other',
];

const REASON_LABELS: Record<EvConventionEditorReasonKind, string> = {
  preferenceUpdate: 'Preference update',
  correction: 'Correction',
  other: 'Other',
};

const PER_SELECT_SEPARATORS: readonly string[] = ['_', '-', '.'];
const NON_ALPHANUMERIC_PATTERN = /[^A-Za-z0-9]/g;
const ALLOWED_CUSTOM_CHAR_PATTERN = /^[A-Za-z0-9_\-.]$/;
const CONTROL_KEYS: readonly string[] = [
  'Backspace',
  'Delete',
  'ArrowLeft',
  'ArrowRight',
  'ArrowUp',
  'ArrowDown',
  'Home',
  'End',
  'Tab',
  'Enter',
  'Escape',
];
const EXTENSION_TOKEN_VALUE = '{extension}';
const CUSTOM_TOKEN_VALUE = '{custom}';
const DOT_LITERAL = '.';

const DEFAULT_EV_CREATE_TEMPLATE: readonly TokenFormat[] = [
  { order: 0, value: '{reference}' },
  { order: 1, value: '_' },
  { order: 2, value: '.' },
  { order: 3, value: '{extension}' },
];

@Component({
  selector: 'app-ev-convention-editor',
  templateUrl: './ev-convention-editor.component.html',
  styleUrls: ['./ev-convention-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: false,
})
export class EvConventionEditorComponent implements OnInit, OnChanges {
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly previewService = inject(FilenameConventionPreviewService);

  @Input() mode: EvConventionEditorMode = 'create';
  @Input() value: FilenameConventionEvBased | null = null;
  @Input() masterSuites: readonly IMasterSuite[] = [];
  @Input() enhancementVariants: readonly EnhancementVariantDetail[] = [];
  @Input() configuredEvIds: readonly string[] = [];
  @Input() organizationId: string = '';

  @Output() readonly payloadChange = new EventEmitter<EvConventionEditorPayload>();

  readonly selectableReasons = SELECTABLE_REASONS;
  readonly reasonLabel = (k: EvConventionEditorReasonKind): string => REASON_LABELS[k];
  readonly templateVocabulary = FILENAME_TOKEN_VOCABULARY_WITH_CUSTOM;
  readonly masterSuiteControl = new FormControl<string | null>(null);
  readonly evControl = new FormControl<string | null>(null);
  readonly templateControl = new FormControl<TokenFormat[]>([], { nonNullable: true });
  readonly reasonControl = new FormControl<EvConventionEditorReasonKind | null>(null, {
    validators: [Validators.required],
  });
  readonly detailControl = new FormControl<string>('', {
    nonNullable: true,
    validators: [Validators.required, Validators.minLength(3)],
  });

  perSelectRows: EvConventionPerSelectRow[] = [];

  ngOnInit(): void {
    this.seedFromValue();
    this.subscribeMsChanges();
    this.subscribeEmitters();
    this.emitPayload();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const valueOrModeChanged =
      (changes['value'] && !changes['value'].firstChange) ||
      (changes['mode'] && !changes['mode'].firstChange);
    if (valueOrModeChanged) {
      this.seedFromValue();
      this.emitPayload();
      return;
    }
    const evListChanged = changes['enhancementVariants'] && !changes['enhancementVariants'].firstChange;
    const msListChanged = changes['masterSuites'] && !changes['masterSuites'].firstChange;
    if ((evListChanged || msListChanged) && this.evControl.value !== null && this.perSelectRows.length === 0) {
      this.rebuildPerSelectRows(this.value?.entries[0]?.selects ?? []);
      this.emitPayload();
    }
  }

  get msLocked(): boolean {
    return this.mode === 'update';
  }

  get evLocked(): boolean {
    return this.mode === 'update';
  }

  get showReasonPicker(): boolean {
    return this.mode === 'update';
  }

  get isOtherReason(): boolean {
    return this.reasonControl.value === 'other';
  }

  get availableEvs(): readonly EnhancementVariantDetail[] {
    const msId = this.masterSuiteControl.value;
    if (msId === null) return [];
    return this.enhancementVariants.filter((ev) => ev.masterSuiteId === msId);
  }

  get showPlaceholder(): boolean {
    return this.mode === 'create' && this.evControl.value === null;
  }

  isEvDisabled(evId: string): boolean {
    return this.configuredEvIds.includes(evId);
  }

  slotTokenLabel(slug: string): string {
    const found = this.templateVocabulary.find((v: TokenVocabularyItem) => v.slug === slug);
    return found?.label ?? slug;
  }

  resetRow(rowIndex: number): void {
    const row = this.perSelectRows[rowIndex];
    if (row === undefined) return;
    const cleared = row.tokenFormats.map((chip) =>
      chip.origin === 'PerSelect'
        ? { order: chip.order, value: '', origin: chip.origin }
        : chip,
    );
    this.commitRowTokens(rowIndex, cleared);
  }

  isChipLocked(chip: TokenFormat): boolean {
    if (chip.origin === 'Template') return true;
    if (chip.origin === 'PerSelect') return false;
    return this.isTokenChip(chip) || this.isSeparatorChip(chip);
  }

  isTokenChip(chip: TokenFormat): boolean {
    return classifyChip(chip.value) === 'token';
  }

  isCustomChip(chip: TokenFormat): boolean {
    return classifyChip(chip.value) === 'custom';
  }

  isSeparatorChip(chip: TokenFormat): boolean {
    return classifyChip(chip.value) === 'separator';
  }

  chipTokenSlug(chip: TokenFormat): string {
    return this.isTokenChip(chip) ? chipLabelOf(chip.value) : '';
  }

  addPerSelectCustomChip(rowIndex: number): void {
    this.appendChip(rowIndex, '');
  }

  addPerSelectSeparator(rowIndex: number, glyph: string): void {
    if (!PER_SELECT_SEPARATORS.includes(glyph)) return;
    this.appendChip(rowIndex, glyph);
  }

  updatePerSelectChip(rowIndex: number, chipIndex: number, value: string): void {
    const row = this.perSelectRows[rowIndex];
    if (row === undefined) return;
    if (chipIndex < 0 || chipIndex >= row.tokenFormats.length) return;
    const target = row.tokenFormats[chipIndex];
    if (this.isChipLocked(target)) return;
    const sanitised = this.sanitiseCustomValue(value);
    if (sanitised === target.value) return;
    const before = row.tokenFormats.slice(0, chipIndex);
    const after = row.tokenFormats.slice(chipIndex + 1);
    const updated: TokenFormat = { order: 0, value: sanitised, origin: 'PerSelect' };
    this.commitRowTokens(rowIndex, [...before, updated, ...after]);
  }

  removePerSelectChip(rowIndex: number, chipIndex: number): void {
    const row = this.perSelectRows[rowIndex];
    if (row === undefined) return;
    if (chipIndex < 0 || chipIndex >= row.tokenFormats.length) return;
    const nextTokens = row.tokenFormats.filter((_, i) => i !== chipIndex);
    this.commitRowTokens(rowIndex, nextTokens);
  }

  dropPerSelectChip(
    rowIndex: number,
    event: { previousIndex: number; currentIndex: number },
  ): void {
    const row = this.perSelectRows[rowIndex];
    if (row === undefined) return;
    const maxIndex = row.tokenFormats.length - 1;
    const clampedTarget = Math.max(0, Math.min(event.currentIndex, maxIndex));
    const clampedSource = Math.max(0, Math.min(event.previousIndex, maxIndex));
    const next = [...row.tokenFormats];
    const [moved] = next.splice(clampedSource, 1);
    next.splice(clampedTarget, 0, moved);
    this.commitRowTokens(rowIndex, next);
  }

  onPerSelectChipInput(rowIndex: number, chipIndex: number, event: Event): void {
    const input = event.target as HTMLInputElement;
    this.updatePerSelectChip(rowIndex, chipIndex, input.value);
  }

  onCustomKeyDown(event: KeyboardEvent): void {
    if (event.ctrlKey || event.metaKey || event.altKey) return;
    if (CONTROL_KEYS.includes(event.key)) return;
    if (event.key.length !== 1) return;
    if (ALLOWED_CUSTOM_CHAR_PATTERN.test(event.key)) return;
    event.preventDefault();
  }

  private appendChip(rowIndex: number, value: string): void {
    const row = this.perSelectRows[rowIndex];
    if (row === undefined) return;
    const insertionIndex = this.trailerStartIndex(row.tokenFormats);
    const before = row.tokenFormats.slice(0, insertionIndex);
    const after = row.tokenFormats.slice(insertionIndex);
    const nextTokens: TokenFormat[] = [
      ...before,
      { order: 0, value, origin: 'PerSelect' },
      ...after,
    ];
    this.commitRowTokens(rowIndex, nextTokens);
  }

  private commitRowTokens(rowIndex: number, nextTokens: readonly TokenFormat[]): void {
    const normalised = nextTokens.map((chip, index) => this.reorder(chip, index));
    this.perSelectRows = this.perSelectRows.map((r, i) =>
      i === rowIndex ? { ...r, tokenFormats: normalised } : r,
    );
    this.recomputeAllPreviews();
    this.emitPayload();
    this.cdr.markForCheck();
  }

  private reorder(chip: TokenFormat, index: number): TokenFormat {
    return chip.origin === undefined
      ? { order: index, value: chip.value }
      : { order: index, value: chip.value, origin: chip.origin };
  }

  sanitizeAlphanumeric(value: string): string {
    return value.replace(NON_ALPHANUMERIC_PATTERN, '');
  }

  private sanitiseCustomValue(value: string): string {
    return Array.from(value)
      .filter((char) => ALLOWED_CUSTOM_CHAR_PATTERN.test(char))
      .join('');
  }

  onTemplateChange(chips: TokenFormat[]): void {
    this.templateControl.setValue(chips);
  }

  private trailerStartIndex(tokens: readonly TokenFormat[]): number {
    const extensionIndex = tokens.findIndex((chip) => chip.value === EXTENSION_TOKEN_VALUE);
    if (extensionIndex < 0) return tokens.length;
    const dotPrecedes =
      extensionIndex > 0 && tokens[extensionIndex - 1].value === DOT_LITERAL;
    return dotPrecedes ? extensionIndex - 1 : extensionIndex;
  }

  private normaliseOrder(tokens: readonly TokenFormat[]): TokenFormat[] {
    return tokens.map((token, index) => this.reorder(token, index));
  }

  private seedFromValue(): void {
    const seed = this.value;
    const entry = seed?.entries[0] ?? null;

    this.masterSuiteControl.setValue(entry?.masterSuiteId ?? null, { emitEvent: false });
    this.evControl.setValue(entry?.masterSuiteEnhancementVariantId ?? null, { emitEvent: false });
    this.templateControl.setValue(
      seed
        ? this.hydrateChips(seed.template)
        : this.mode === 'create'
          ? DEFAULT_EV_CREATE_TEMPLATE.map((t) => ({ ...t }))
          : [],
      { emitEvent: false },
    );
    this.reasonControl.setValue(null, { emitEvent: false });
    this.detailControl.setValue('', { emitEvent: false });

    if (this.msLocked) this.masterSuiteControl.disable({ emitEvent: false });
    if (this.evLocked) this.evControl.disable({ emitEvent: false });

    this.rebuildPerSelectRows(entry?.selects ?? []);
  }

  private hydrateChips(source: readonly TokenFormat[]): TokenFormat[] {
    return [...source]
      .sort((a, b) => a.order - b.order)
      .map((t, i) => this.reorder(t, i));
  }

  private hydrateChipsWithOrigin(
    source: readonly TokenFormat[],
    fallbackOrigin: TokenFormatOrigin,
  ): TokenFormat[] {
    return [...source]
      .sort((a, b) => a.order - b.order)
      .map((t, i) => ({
        order: i,
        value: t.value,
        origin: t.origin ?? fallbackOrigin,
      }));
  }

  private rebuildPerSelectRows(existingSelects: readonly FilenameConventionPerSelect[]): void {
    const evId = this.evControl.value;
    if (evId === null) {
      this.perSelectRows = [];
      return;
    }
    const ev = this.enhancementVariants.find((candidate) => candidate.id === evId);
    if (ev === undefined) {
      this.perSelectRows = [];
      return;
    }

    this.perSelectRows = ev.masterSuiteEnhancementVariantAssets.map((asset) => {
      const seededSelect = existingSelects.find(
        (s) => s.masterSuiteEnhancementVariantAssetId === asset.id,
      );
      const tokenFormats = seededSelect
        ? this.materialiseSeededTokenFormats(seededSelect as LegacyPerSelect)
        : this.createDefaultRowTokenFormats();
      return {
        asset,
        tokenFormats,
        preview: { value: null, error: null },
      };
    });
    this.recomputeAllPreviews();
    this.cdr.markForCheck();
  }

  private materialiseSeededTokenFormats(seed: LegacyPerSelect): TokenFormat[] {
    if (seed.tokenFormats !== undefined) {
      return this.hydrateChipsWithOrigin(seed.tokenFormats, 'PerSelect');
    }
    const template = this.templateControl.value;
    const customValues = seed.customValues ?? [];
    const extraChips = this.hydrateChipsWithOrigin(seed.extraChips ?? [], 'PerSelect');
    const materialisedTemplate = this.materialiseTemplateWithCustoms(template, customValues);
    const trailerStart = this.trailerStartIndex(materialisedTemplate);
    const head = materialisedTemplate.slice(0, trailerStart);
    const trailer = materialisedTemplate.slice(trailerStart);
    return this.normaliseOrder([...head, ...extraChips, ...trailer]);
  }

  private materialiseTemplateWithCustoms(
    template: readonly TokenFormat[],
    customValues: readonly string[],
  ): TokenFormat[] {
    let customIndex = 0;
    const ordered = [...template].sort((a, b) => a.order - b.order);
    return ordered.map((chip) => {
      if (chip.value === CUSTOM_TOKEN_VALUE) {
        const value = customIndex < customValues.length ? customValues[customIndex] : '';
        customIndex++;
        return { order: chip.order, value, origin: 'PerSelect' };
      }
      return { order: chip.order, value: chip.value, origin: 'Template' };
    });
  }

  private createDefaultRowTokenFormats(): TokenFormat[] {
    const mirror = this.templateAsMirrorTokens();
    const insertionIndex = this.trailerStartIndex(mirror);
    const before = mirror.slice(0, insertionIndex);
    const after = mirror.slice(insertionIndex);
    const initialPerSelect: TokenFormat = { order: 0, value: '', origin: 'PerSelect' };
    return this.normaliseOrder([...before, initialPerSelect, ...after]);
  }

  private templateAsMirrorTokens(): TokenFormat[] {
    const template = this.templateControl.value;
    return this.materialiseTemplateWithCustoms(template, []).map((chip, index) => ({
      order: index,
      value: chip.value,
      origin: chip.origin ?? 'Template',
    }));
  }

  private syncTemplateMirrorsAcrossRows(): void {
    const mirrorTokens = this.templateAsMirrorTokens();
    this.perSelectRows = this.perSelectRows.map((row) => ({
      ...row,
      tokenFormats: this.applyTemplateMirror(row.tokenFormats, mirrorTokens),
    }));
  }

  private applyTemplateMirror(
    rowTokens: readonly TokenFormat[],
    mirrorTokens: readonly TokenFormat[],
  ): TokenFormat[] {
    const perSelectChips = rowTokens.filter((chip) => chip.origin === 'PerSelect');
    const trailerStart = this.trailerStartIndex(mirrorTokens);
    const head = mirrorTokens.slice(0, trailerStart);
    const trailer = mirrorTokens.slice(trailerStart);
    return this.normaliseOrder([...head, ...perSelectChips, ...trailer]);
  }

  private subscribeMsChanges(): void {
    this.masterSuiteControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.evControl.setValue(null, { emitEvent: false });
        this.perSelectRows = [];
        this.emitPayload();
      });

    this.evControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.rebuildPerSelectRows([]);
        this.recomputeAllPreviews();
        this.emitPayload();
      });
  }

  private subscribeEmitters(): void {
    this.templateControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.syncTemplateMirrorsAcrossRows();
        this.recomputeAllPreviews();
        this.emitPayload();
      });
    this.reasonControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.emitPayload());
    this.detailControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.emitPayload());
  }

  private emitPayload(): void {
    const conventionMsEv = this.readMsEv();
    const template = this.normaliseOrder(this.templateControl.value);
    const perSelectRows = this.perSelectRows.map((row) => this.readPerSelect(row));
    const writeReason = this.readWriteReason();
    const payload: EvConventionEditorPayload = {
      mode: 'evBased',
      conventionMsEv,
      template,
      perSelectRows,
      writeReason,
    };
    if (isDevMode()) {
      console.log('[EV editor] emit', {
        conventionMsEv,
        hasField: 'conventionMsEv' in payload,
        type: typeof payload.conventionMsEv,
      });
    }
    this.payloadChange.emit(payload);
  }

  private readMsEv(): EvConventionMsEv | null {
    const ms = this.masterSuiteControl.value;
    const ev = this.evControl.value;
    if (ms === null || ev === null) return null;
    return { masterSuiteId: ms, masterSuiteEnhancementVariantId: ev };
  }

  private readPerSelect(row: EvConventionPerSelectRow): FilenameConventionPerSelect {
    return {
      masterSuiteEnhancementVariantAssetId: row.asset.id,
      tokenFormats: row.tokenFormats.map((chip, index) => this.reorder(chip, index)),
    };
  }

  private readWriteReason(): FilenameConventionWriteReason | null {
    if (!this.showReasonPicker) return null;
    const kind = this.reasonControl.value;
    if (kind === null) return null;
    if (kind === 'other') return { kind: 'other', detail: this.detailControl.value.trim() };
    return { kind };
  }

  private recomputeAllPreviews(): void {
    this.perSelectRows = this.perSelectRows.map((row) => ({
      ...row,
      preview: this.computeRowPreview(row),
    }));
  }

  private computeRowPreview(row: EvConventionPerSelectRow): RenderResult {
    const msEv = this.readMsEv();
    if (msEv === null) {
      return { value: null, error: null };
    }
    const payload: FilenameConventionEvBased = {
      mode: 'evBased',
      template: this.normaliseOrder(this.templateControl.value),
      entries: [
        {
          masterSuiteId: msEv.masterSuiteId,
          masterSuiteEnhancementVariantId: msEv.masterSuiteEnhancementVariantId,
          selects: this.perSelectRows.map((r) => this.readPerSelect(r)),
        },
      ],
    };
    return this.previewService.renderTokenForm(
      payload,
      msEv.masterSuiteId,
      msEv.masterSuiteEnhancementVariantId,
      row.asset.id,
    );
  }
}
